'use client';

import { useEffect, useRef, type RefObject } from 'react';

export interface UseTrackInViewOptions {
  /** Visibility threshold (0–1). Default: 0.5 */
  threshold?: number;
  /** Fired exactly once when the observed element first crosses the threshold. */
  onView: () => void;
}

/**
 * One-shot IntersectionObserver hook.
 *
 * Attach the returned ref to an element. When the element first becomes
 * visible at >= `threshold` of the viewport, `onView` is invoked once and
 * the observer disconnects.
 *
 * SSR / non-DOM environments without IntersectionObserver are a no-op.
 *
 * The `onView` callback is read from a ref so the effect doesn't re-fire
 * on callback identity changes — only `threshold` is a real dependency.
 */
export function useTrackInView<T extends Element = HTMLElement>(
  options: UseTrackInViewOptions,
): RefObject<T | null> {
  const ref = useRef<T | null>(null);
  const onViewRef = useRef(options.onView);

  // Keep latest callback without retriggering the effect
  useEffect(() => {
    onViewRef.current = options.onView;
  });

  const threshold = options.threshold ?? 0.5;

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const el = ref.current;
    if (!el) return;

    let fired = false;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (
            !fired &&
            entry.isIntersecting &&
            entry.intersectionRatio >= threshold
          ) {
            fired = true;
            onViewRef.current();
            observer.disconnect();
            break;
          }
        }
      },
      { threshold },
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [threshold]);

  return ref;
}
